import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LandingPage } from './components/LandingPage';
import { SessionLinksPage } from './components/SessionLinksPage';
import { MasterView } from './components/MasterView';
import { TeamView } from './components/TeamView';
import { LanguageSelector } from './components/LanguageSelector';
import { Button } from './components/ui/button';
import { useGameState } from './hooks/use-game-state';
import type { GameRole } from './types/game';
import './i18n';

interface UrlParams {
  role: GameRole | null;
  teamId: string | null;
  sessionId: string;
  userId: string | null;
  userName: string | null;
}

function getUrlParams(): UrlParams {
  const searchParams = new URLSearchParams(window.location.search);
  
  const params = {
    role: searchParams.get('role') as GameRole || null,
    teamId: searchParams.get('team') || null,
    sessionId: searchParams.get('session') || 'default',
    userId: searchParams.get('userId') || null,
    userName: searchParams.get('userName') || null
  };

  // Debug logs
  console.log('URL Parsing Debug:');
  console.log('- Current URL:', window.location.href);
  console.log('- Pathname:', window.location.pathname);
  console.log('- Search:', window.location.search);
  console.log('- Parsed params:', params);

  return params;
}

export default function App() {
  const { t } = useTranslation();
  const [role, setRole] = useState<GameRole | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState('default');
  const [userId, setUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [showSessionLinks, setShowSessionLinks] = useState(false);

  // Get URL parameters on load
  useEffect(() => {
    const { 
      role: urlRole, 
      teamId: urlTeamId, 
      sessionId: urlSessionId,
      userId: urlUserId,
      userName: urlUserName
    } = getUrlParams();

    console.log('Setting state from URL params:');
    console.log('- urlRole:', urlRole);
    console.log('- urlTeamId:', urlTeamId);
    console.log('- urlSessionId:', urlSessionId);
    console.log('- urlUserId:', urlUserId);
    console.log('- urlUserName:', urlUserName);

    if (urlRole) setRole(urlRole);
    if (urlTeamId) setTeamId(urlTeamId);
    if (urlSessionId) setSessionId(urlSessionId);
    if (urlUserId) setUserId(urlUserId);
    if (urlUserName) setUserName(urlUserName);

    console.log('State after setting:');
    console.log('- role:', urlRole);
    console.log('- teamId:', urlTeamId);
    console.log('- sessionId:', urlSessionId);
    console.log('- userId:', urlUserId);
    console.log('- userName:', urlUserName);
    console.log('- showSessionLinks:', showSessionLinks);
  }, []);

  // Handle game initialization (master only)
  const handleInitialize = ({
    role: newRole,
    sessionId: newSessionId,
    userId: newUserId,
    userName: newUserName
  }: {
    role: GameRole;
    sessionId: string;
    userId: string;
    userName: string;
  }) => {
    setRole(newRole);
    setSessionId(newSessionId);
    setUserId(newUserId);
    setUserName(newUserName);
    setShowSessionLinks(true);
  };

  // Handle new session request
  const handleNewSession = () => {
    setShowSessionLinks(false);
    setSessionId('default');
  };

  // Initialize game state
  const gameState = useGameState({
    role: role || 'team',
    teamId: teamId || undefined,
    sessionId,
    userId: userId || undefined,
    userName: userName || undefined
  });

  // Handle loading state
  if (!gameState.state && gameState.status === 'connecting') {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <LanguageSelector />
        <div className="text-center">
          <h2 className="mb-2 text-2xl font-bold">{t('common.loading')}</h2>
          <p className="text-muted-foreground">{t('game.messages.connecting')}</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (gameState.status === 'error') {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <LanguageSelector />
        <div className="text-center">
          <h2 className="mb-2 text-2xl font-bold text-red-500">{t('common.error')}</h2>
          <p className="text-muted-foreground">{t('game.messages.connectionError')}</p>
          <Button
            onClick={() => {
              const basePath = import.meta.env.PROD ? '/storm' : '';
              window.location.href = `${window.location.origin}${basePath}${window.location.pathname}${window.location.search}`;
            }}
            className="mt-4"
          >
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  // Show landing page for master initialization
  if (!role || !userId || !userName) {
    return (
      <div className="relative">
        <LanguageSelector />
        <LandingPage onInitialize={handleInitialize} />
      </div>
    );
  }

  // Show session links page for master
  if (role === 'master' && showSessionLinks) {
    return (
      <div className="relative">
        <LanguageSelector />
        <SessionLinksPage 
          sessionId={sessionId}
          onNewSession={handleNewSession}
        />
      </div>
    );
  }

  // Show appropriate view based on role
  return (
    <div className="relative">
      <LanguageSelector />
      {role === 'master' ? (
        <MasterView
          {...gameState}
        />
      ) : (
        <TeamView
          gameState={gameState}
          teamId={teamId || ''}
          sessionId={sessionId}
          userId={userId}
          userName={userName}
        />
      )}
    </div>
  );
}
